import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortstayData, upsertShortstay } from "../../../../redux/shortstaysSlice";
import {
  TrashSimple,
  Lightbulb,
  LockSimple,
  KeyReturn,
  NotePencil,
  CheckCircle,
} from "phosphor-react";
import { Towel } from "@phosphor-icons/react";

const options = [
  { key: "gatherTowels", label: "Gather used towels",  icon: <Towel size={26} /> },
  { key: "throwTrash",   label: "Throw trash away",    icon: <TrashSimple size={26} /> },
  { key: "turnOff",      label: "Turn things off",      icon: <Lightbulb size={26} /> },
  { key: "lockUp",       label: "Lock up",              icon: <LockSimple size={26} /> },
  { key: "returnKeys",   label: "Return keys",          icon: <KeyReturn size={26} /> },
  { key: "other",        label: "Additional requests",  icon: <NotePencil size={26} /> },
];

const T = {
  bg:       "#FFFFFF",
  text:     "#0A0A0A",
  textSoft: "#444444",
  muted:    "#9A9A9A",
  border:   "#EBEBEB",
  gold:     "#C9A84C",
  goldDark: "#9A7A2E",
  black:    "#0A0A0A",
  sans:     "'Montserrat', sans-serif",
};

const CheckoutInstructions: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const currentListing = useSelector((s: RootState) => s.shortstays.currentListing);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [details,      setDetails]      = useState<{ [key: string]: string }>({});
  const [activeKey,    setActiveKey]    = useState<string | null>(null);
  const [textInput,    setTextInput]    = useState("");
  const [hasChanges,   setHasChanges]   = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);

  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
  }, [dispatch, listingId]);

  useEffect(() => {
    const existing = currentListing?.checkoutInstructions || {};
    setSelectedKeys(existing.selected || []);
    setDetails(existing.details || {});
  }, [currentListing]);

  const openTextModal = (key: string) => {
    setActiveKey(key);
    setTextInput(details[key] || "");
  };

  const toggleOption = (key: string) => {
    if (key === "lockUp" || key === "returnKeys" || key === "other") {
      openTextModal(key);
    } else {
      setSelectedKeys(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
      );
      setHasChanges(true);
    }
  };

  const handleTextSave = () => {
    if (!activeKey) return;
    const updated = { ...details, [activeKey]: textInput };
    if (!selectedKeys.includes(activeKey)) setSelectedKeys([...selectedKeys, activeKey]);
    setDetails(updated);
    setHasChanges(true);
    setActiveKey(null);
    setTextInput("");
  };

  const handleSave = async () => {
    if (!listingId || !hasChanges) return;
    setSaving(true);
    await dispatch(upsertShortstay({
      listingId,
      data: { checkoutInstructions: { selected: selectedKeys, details } },
    }));
    setSaving(false);
    setHasChanges(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const isSelected = (key: string) => selectedKeys.includes(key);

  return (
    <div style={{ fontFamily: T.sans, color: T.text, maxWidth: 700, margin: "0 auto", position: "relative" }}>

      {/* ── SAVING OVERLAY ── */}
      {saving && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(255,255,255,0.65)",
          backdropFilter: "blur(6px)",
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 20,
        }}>
          <div style={{
            width: 56, height: 56,
            borderRadius: "50%",
            border: `3px solid ${T.border}`,
            borderTop: `3px solid ${T.black}`,
            animation: "spin 0.8s linear infinite",
          }} />
          <p style={{
            fontSize: 12, fontWeight: 500,
            color: T.black, letterSpacing: "0.08em",
            textTransform: "uppercase", margin: 0,
          }}>
            Saving
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ── SUCCESS TOAST ── */}
      {saved && (
        <div style={{
          position: "fixed",
          bottom: 32, left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1200,
          background: "#FFFFFF",
          border: "1px solid #D4EDDA",
          borderRadius: 12,
          padding: "14px 22px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
          minWidth: 220,
        }}>
          <CheckCircle size={20} weight="fill" color="#22883F" />
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#22883F", margin: 0 }}>
              Successfully saved
            </p>
            <p style={{ fontSize: 11, color: T.muted, margin: "2px 0 0" }}>
              Checkout instructions updated
            </p>
          </div>
        </div>
      )}

      {/* heading */}
      <div style={{ marginBottom: 28 }}>
        <p style={{
          fontSize: 9, color: T.muted, letterSpacing: "0.13em",
          textTransform: "uppercase", margin: "0 0 6px",
        }}>
          Guest experience
        </p>
        <h2 style={{ fontSize: 20, fontWeight: 500, color: T.black, margin: 0 }}>
          Checkout instructions
        </h2>
        <p style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
          Select what you'd like guests to do before they leave.
        </p>
      </div>

      {/* card grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 16,
        marginBottom: 32,
      }}>
        {options.map(({ key, label, icon }) => {
          const active = isSelected(key);
          return (
            <div
              key={key}
              onClick={() => toggleOption(key)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 14,
                padding: "20px 18px",
                borderRadius: 14,
                cursor: "pointer",
                transition: "all .2s ease",
                background: active ? T.black : "#FFFFFF",
                border: active ? `1.5px solid ${T.black}` : `1px solid ${T.border}`,
                boxShadow: active
                  ? "0 8px 24px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.06)"
                  : "0 4px 14px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.05)",
                transform: active ? "translateY(-2px)" : "translateY(0)",
                minHeight: 130,
                position: "relative",
              }}
            >
              {/* icon circle */}
              <div style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: active ? "rgba(201,168,76,0.15)" : "#F5F5F5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: active ? T.gold : T.black,
                flexShrink: 0,
                transition: "all .2s ease",
              }}>
                {icon}
              </div>

              <div>
                <p style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: active ? "#FFFFFF" : T.black,
                  margin: 0,
                  lineHeight: 1.3,
                  transition: "color .2s",
                }}>
                  {label}
                </p>
                {details[key] && (
                  <p style={{
                    fontSize: 11,
                    color: active ? T.gold : T.muted,
                    margin: "4px 0 0",
                    lineHeight: 1.4,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}>
                    {details[key]}
                  </p>
                )}
              </div>

              {/* selected dot */}
              {active && (
                <div style={{
                  position: "absolute",
                  top: 14, right: 14,
                  width: 7, height: 7,
                  borderRadius: "50%",
                  background: T.gold,
                  boxShadow: `0 0 8px ${T.gold}`,
                }} />
              )}
            </div>
          );
        })}
      </div>

      {/* save button */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{
            background: hasChanges ? T.black : "#F0F0F0",
            color: hasChanges ? "#FFFFFF" : T.muted,
            border: "none",
            borderRadius: 10,
            padding: "11px 28px",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: T.sans,
            cursor: hasChanges ? "pointer" : "not-allowed",
            letterSpacing: "0.04em",
            transition: "all .2s ease",
            boxShadow: hasChanges ? "0 4px 14px rgba(0,0,0,0.18)" : "none",
          }}
        >
          Save changes
        </button>
      </div>

      {/* modal overlay */}
      {activeKey && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.35)",
          backdropFilter: "blur(4px)",
          zIndex: 999,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
        }}>
          <div style={{
            background: "#FFFFFF",
            borderRadius: 16,
            padding: 28,
            width: "100%",
            maxWidth: 420,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2), 0 4px 16px rgba(0,0,0,0.1)",
            border: `1px solid ${T.border}`,
          }}>
            <p style={{
              fontSize: 9, color: T.muted, letterSpacing: "0.13em",
              textTransform: "uppercase", margin: "0 0 6px",
            }}>
              Instruction detail
            </p>
            <h3 style={{ fontSize: 15, fontWeight: 500, color: T.black, margin: "0 0 18px" }}>
              {options.find(o => o.key === activeKey)?.label}
            </h3>

            <textarea
              placeholder="Describe this instruction..."
              value={textInput}
              onChange={e => setTextInput(e.target.value)}
              style={{
                width: "100%",
                height: 110,
                padding: "12px 14px",
                borderRadius: 10,
                border: `1px solid ${T.border}`,
                background: "#FAFAFA",
                color: T.black,
                fontFamily: T.sans,
                fontSize: 13,
                resize: "none",
                outline: "none",
                marginBottom: 18,
                boxSizing: "border-box",
                lineHeight: 1.6,
              }}
            />

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => { setActiveKey(null); setTextInput(""); }}
                style={{
                  background: "transparent",
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  padding: "9px 18px",
                  fontSize: 12,
                  fontFamily: T.sans,
                  color: T.textSoft,
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleTextSave}
                style={{
                  background: T.black,
                  border: "none",
                  borderRadius: 8,
                  padding: "9px 20px",
                  fontSize: 12,
                  fontFamily: T.sans,
                  color: "#FFFFFF",
                  cursor: "pointer",
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutInstructions;